import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  ExternalLink,
  FileText,
  Plus,
  ScrollText,
  BookOpen,
  Network,
  ShieldCheck,
  KeyRound,
  ReceiptText,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { useAllDocuments } from '@/lib/documents';
import { PageHeader } from '@/components/page-header';
import { PickClientDialog } from '@/components/pick-client-dialog';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';

const CATEGORY_LABEL: Record<string, string> = {
  contract: 'Contract',
  msa: 'MSA',
  sow: 'SOW',
  runbook: 'Runbook',
  diagram: 'Diagram',
  warranty: 'Warranty',
  license: 'License',
  receipt: 'Receipt',
  other: 'Other',
};

const CATEGORY_ICON: Record<string, ComponentType<LucideProps>> = {
  contract: FileText,
  msa: FileText,
  sow: ScrollText,
  runbook: BookOpen,
  diagram: Network,
  warranty: ShieldCheck,
  license: KeyRound,
  receipt: ReceiptText,
  other: FileText,
};

const CATEGORY_BUCKET: Record<string, string> = {
  contract: 'bg-blue-500/12 text-blue-700 dark:text-blue-300',
  msa: 'bg-blue-500/12 text-blue-700 dark:text-blue-300',
  sow: 'bg-violet-500/12 text-violet-700 dark:text-violet-300',
  runbook: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
  diagram: 'bg-cyan-500/12 text-cyan-700 dark:text-cyan-300',
  warranty: 'bg-amber-500/12 text-amber-700 dark:text-amber-300',
  license: 'bg-fuchsia-500/12 text-fuchsia-700 dark:text-fuchsia-300',
  receipt: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
  other: 'bg-muted text-muted-foreground',
};

export function DocumentsIndex() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [pickOpen, setPickOpen] = useState(false);
  const { data: paginated, isLoading } = useAllDocuments({
    q: q || undefined,
    category: category || undefined,
    page,
  });
  const docs = paginated?.data;
  const meta = paginated?.meta;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="Documents"
        subtitle="Every document across every client."
        action={
          <Button onClick={() => setPickOpen(true)}>
            <Plus className="size-4" /> Upload document
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by title or description…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All categories</option>
          {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !docs || docs.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents"
          description="Pick a client to upload the first one."
          action={
            <Button onClick={() => setPickOpen(true)}>
              <Plus className="size-4" /> Upload document
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {docs.map((d) => {
              const expired = d.days_until_expiry != null && d.days_until_expiry < 0;
              const soon = d.days_until_expiry != null && d.days_until_expiry <= 30 && !expired;
              const Icon = CATEGORY_ICON[d.category] ?? FileText;
              const bucket = CATEGORY_BUCKET[d.category] ?? 'bg-muted text-muted-foreground';
              return (
                <DataListRow
                  key={d.id}
                  onClick={() => d.file_url && window.open(d.file_url, '_blank')}
                  interactive={!!d.file_url}
                >
                  <div className={`flex size-8 shrink-0 items-center justify-center rounded-md ${bucket}`}>
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate font-medium text-foreground">{d.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {CATEGORY_LABEL[d.category] ?? d.category}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <Link
                        to={`/clients/${d.client_id}`}
                        className="hover:text-foreground hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {d.client_name ?? `Client #${d.client_id}`}
                      </Link>
                      {d.description ? (
                        <>
                          <span aria-hidden>·</span>
                          <span className="truncate">{d.description}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  {d.expires_at ? (
                    <StatusPill tone={expired ? 'danger' : soon ? 'warning' : 'neutral'}>
                      {expired ? (
                        <AlertTriangle className="size-3" />
                      ) : (
                        <CalendarClock className="size-3" />
                      )}
                      {expired
                        ? `Expired ${Math.abs(d.days_until_expiry!)} d ago`
                        : `${d.days_until_expiry} d`}
                    </StatusPill>
                  ) : null}
                  {d.file_url ? (
                    <a
                      href={d.file_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Open ${d.title} in a new tab`}
                      className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100"
                    >
                      <ExternalLink className="size-4" />
                    </a>
                  ) : null}
                </DataListRow>
              );
            })}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
      <PickClientDialog
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        title="Upload document"
        tab="documents"
      />
    </div>
  );
}
